import { api } from '@/api/client';
import type { AuthUser } from '@/types/auth';
import type { Address, AddressPayload, ProfilePayload } from '@/types/account';

/**
 * The customer's own account — BUILD-PLAN §16, §31.
 *
 * Every endpoint here acts on the caller and only the caller: there is no
 * user id in any path. That is the API's design rather than this client's
 * politeness, but it is worth stating where the calls are written.
 */
export const accountKeys = {
  addresses: ['account', 'addresses'] as const,
};

export function fetchAddresses(): Promise<Address[]> {
  return api.get<Address[]>('/addresses');
}

export function createAddress(payload: AddressPayload): Promise<Address> {
  return api.post<Address>('/addresses', payload);
}

export function updateAddress(id: number, payload: AddressPayload): Promise<Address> {
  return api.put<Address>(`/addresses/${id}`, payload);
}

export function deleteAddress(id: number): Promise<null> {
  return api.delete<null>(`/addresses/${id}`);
}

/**
 * Promote an address to the default.
 *
 * A dedicated endpoint rather than `PUT` with `is_default: true`, because
 * demoting whatever held the flag has to happen in the same transaction —
 * two addresses both marked default is a state the checkout cannot resolve.
 */
export function makeAddressDefault(id: number): Promise<Address> {
  return api.post<Address>(`/addresses/${id}/default`);
}

export function updateProfile(payload: ProfilePayload): Promise<{ user: AuthUser }> {
  return api.put<{ user: AuthUser }>('/auth/profile', payload);
}
